import React from "react";
import "../DashboardStyles.css";

export default function Chapter3Dashboard() {
  return (
    <div className="chapter chapter3">
      <div className="chapter-content">
        <p className="chapter-eyebrow">Chapter 3</p>
        <h2>Natural Assets</h2>

        <figure className="ceds-figure">
          <img
            src="/images/ceds/rangeland.jpg"
            alt="Rolling rangeland with a two-track dirt road under a storm-darkened sky"
            loading="lazy"
          />
          <figcaption>
            Rangeland in the Nebraska Panhandle. Agriculture and the natural
            systems that support it remain the region’s economic foundation.
          </figcaption>
        </figure>

        <h3>3.1 Introduction</h3>
        <p>
          The Nebraska Panhandle is a region of remarkable and diverse natural
          wealth. Stretching across the High Plains of western Nebraska, the
          region encompasses millions of acres of cropland and native rangeland,
          one of the world’s most critical underground water systems, a
          constellation of nationally significant natural landmarks and
          recreational areas, and an energy resource base (both renewable and
          fossil fuel) that remains substantially underdeveloped relative to its
          potential. These natural assets are not merely scenic or ecological in
          value; they are the foundational pillars of the regional economy and
          the raw material from which future economic development opportunities
          can be built.
        </p>

        <h3>3.2 Agriculture and Land Resources</h3>
        <p>
          Agriculture is the defining economic and cultural force of the
          Nebraska Panhandle. The region’s vast landscape supports a diverse
          agricultural economy anchored by irrigated crop production, dryland
          farming, and extensive cattle ranching and grazing operations. In the
          eastern and river-valley portions (particularly in Scotts Bluff,
          Morrill, Box Butte, and Cheyenne counties) irrigated crop production
          dominates, supported by access to both surface water from the North
          Platte River system and groundwater from the High Plains Aquifer.
          Moving westward and northward into Sioux, Dawes, Sheridan, and Banner
          counties, rangeland and dryland farming become increasingly prevalent.
        </p>
        <p>
          The Panhandle is one of Nebraska’s premier sugar beet production
          areas, with Western Sugar Cooperative’s Scottsbluff processing facility
          serving as a major regional employer and agricultural anchor. Dry
          edible bean production (particularly pinto beans) is another
          regionally significant crop for which the Panhandle has a long history
          and national market presence. These specialty crops, alongside corn,
          hay, and small grains, create a more diversified agricultural revenue
          base than is typical of many Nebraska farming regions.
        </p>
        <p>
          Cattle ranching is the dominant agricultural land use across the
          northern and western portions of the Panhandle. Nebraska as a whole
          ranks among the nation’s top cattle-producing states, with
          approximately 6 million head of cattle and calves statewide as of
          2025. Long-established ranching operations in Sioux, Dawes, Sheridan,
          Banner, and Garden counties have shaped the region’s culture, economy,
          and landscape for more than a century. Despite its foundational
          strength, Panhandle agriculture faces structural challenges including
          farm consolidation, succession-planning difficulties, and the need to
          add value to agricultural output through processing, direct
          marketing, and agricultural tourism.
        </p>

        <h3>3.3 Water Resources</h3>
        <p>
          Water is one of the Nebraska Panhandle’s most critical economic assets
          and long-term vulnerabilities. The region overlies the High Plains
          Aquifer, including the Ogallala Formation, one of the nation’s largest
          groundwater systems. Nebraska contains roughly 37 percent of the
          aquifer’s geographic area but approximately two-thirds of its
          recoverable water in storage. While the aquifer receives natural
          recharge, rates vary substantially by location and may not offset
          withdrawals in areas of intensive use. Much of the groundwater in
          storage is also thousands of years old, underscoring the importance of
          long-term conservation and management. Nebraska’s locally governed
          Natural Resources District (NRD) system provides a distinctive
          framework for managing groundwater and integrating water conservation
          with economic and agricultural needs. The North Platte River and its
          associated irrigation and reservoir systems form the region’s
          principal surface-water resource. The Panhandle is served primarily by
          the North Platte NRD, Twin Platte NRD, and Upper Niobrara-White NRD.
        </p>
        <p className="source-note">
          Source note: High Plains Aquifer extent, water-in-storage, and
          recharge data from U.S. Geological Survey (USGS); groundwater age and
          hydrologic characteristics from USGS; Natural Resources District
          structure and responsibilities from Nebraska Association of Resources
          Districts (NARD); Panhandle NRD service areas from NARD.
        </p>

        <h3>3.4 Natural Landmarks and Outdoor Recreation</h3>
        <p>
          The Nebraska Panhandle is home to a remarkable concentration of
          natural landmarks, geological features, and outdoor recreation assets.
          According to the Bureau of Economic Analysis, Nebraska’s tourism
          sector generated $4.6 billion in GDP in 2023–2024, placing the state
          sixth in the country for GDP growth in accommodation, food services,
          arts, entertainment, and recreation sectors. Tourism is Nebraska’s
          third-largest industry overall, and the second-largest industry in
          many rural areas of the state.
        </p>
        <p>
          Scotts Bluff National Monument welcomed 189,829 visitors in 2023.
          Together with Agate Fossil Beds National Monument, these two sites
          generated $12,990,000 in direct visitor spending in surrounding
          communities, supporting 171 local jobs and contributing a total
          economic impact of $15,318,000 to the regional economy. Chimney Rock
          National Historic Site, Fort Robinson State Park, the Pine Ridge
          National Recreation Area, Toadstool Geologic Park, and the Wildcat
          Hills State Recreation Area round out a tourism asset base of national
          significance.
        </p>
        <p>
          Nebraska’s new statewide tourism marketing campaign, “Nebraska. All
          Heart.,” launched in February 2026, represents an opportunity for the
          Panhandle to align its local destination marketing with a well-funded
          statewide promotion effort. The Nebraska Passport Program has
          demonstrated its ability to triple visitation and double sales for
          participating small communities — a model directly applicable to
          Harrison, Crawford, and communities along the Highway 92 Oregon Trail
          corridor.
        </p>

        <h3>3.5 Energy Resources</h3>
        <p>
          The Nebraska Panhandle possesses one of the most diverse energy
          resource portfolios in the state, spanning conventional fossil fuel
          production, a major freight corridor for coal, and an exceptional but
          largely unrealized renewable energy base.
        </p>

        <h4>Conventional Oil and Natural Gas</h4>
        <p>
          The Panhandle is Nebraska’s dominant petroleum-producing region. The
          region produces more than half of the state’s crude oil and nearly all
          of its natural gas, with production concentrated in Cheyenne, Kimball,
          Banner, and Deuel counties. This production base supports direct
          employment in extraction and field services, generates significant
          royalty and severance-tax revenue for county governments, and anchors
          the supply-chain economy of several communities in the southern
          Panhandle. Long-term production trends depend on commodity prices,
          drilling technology, and regulatory environment (factors that extend
          beyond regional control) but the existing production base represents
          a current economic asset of substantial scale.
        </p>

        <h4>Coal Transportation Corridor</h4>
        <p>
          The region also serves as a major transportation corridor for Powder
          River Basin coal. The BNSF Railway mainline carries significant coal
          shipments to utilities across the Midwest, making the Panhandle’s rail
          corridor economically important to BNSF operations and to the
          communities along the line. The long-term demand for coal
          transportation is, however, subject to significant uncertainty as
          electric utilities shift their generation mix; the SWOT analysis
          (Chapter 8) identifies coal-transport decline as a threat to BNSF
          freight volumes and associated railroad employment.
        </p>

        <h4>Renewable Energy Potential</h4>
        <p>
          The Panhandle holds exceptional renewable energy potential that has
          not yet been converted to economic development at scale. Western
          Nebraska receives the highest solar irradiance in the state and ranks
          among the strongest solar resource areas in the nation. Nebraska as a
          whole ranks fourth nationally in wind energy potential, and the
          Panhandle contains extensive areas well-suited for utility-scale wind
          development — favorable wind resources, available land, and relatively
          low population density combine to make the region a strong candidate
          for wind development if infrastructure barriers can be resolved.
        </p>
        <p>
          Despite these advantages, renewable energy development has remained
          limited. The most significant constraint is the lack of adequate
          electric transmission capacity needed to move additional generation to
          regional and interstate markets. The existing grid, operated by the
          Nebraska Public Power District (NPPD) and Panhandle Rural Electric
          Membership Association (PREMA), was designed around in-region load,
          not export. Expanding transmission infrastructure will be critical to
          unlocking the region’s renewable energy potential, diversifying the
          local economy, and supporting Nebraska’s long-term energy
          competitiveness. The economic development implications of resolving
          this constraint — construction employment, operations and maintenance
          jobs, landowner lease income, and property-tax revenue for fiscally
          stressed counties — are developed further in Chapter 5 (§5.7).
        </p>
        <p className="source-note">
          Source note: Production share figures from Nebraska Oil and Gas
          Conservation Commission (NOGCC); solar irradiance ranking from National
          Renewable Energy Laboratory (NREL) solar resource data; wind potential
          ranking from NREL Wind Energy Resource Atlas; NPPD and PREMA service
          territory from Nebraska Power Review Board.
        </p>

        <h3>3.6 Synthesis: Natural Assets as an Economic Development Foundation</h3>
        <p>
          Several cross-cutting themes emerge from this natural asset inventory.
          First, water security is the most critical and irreplaceable natural
          asset the region possesses. Second, the region’s natural landmarks and
          outdoor recreation assets are significantly undervalued as economic
          development resources. Third, the energy resource base, particularly
          wind and solar, represents a major unrealized economic opportunity
          requiring transmission infrastructure investment. Fourth,
          agriculture’s long-term vitality depends on the sustained health of
          the underlying natural systems (soil, water, and rangeland) that make
          production possible.
        </p>
      </div>
    </div>
  );
}
